import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Edit, Plus, Search, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { adminInputClass } from '@/components/admin/AdminFormField';
import { ConfirmModal } from '@/components/admin/ConfirmModal';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAdminFetch } from '@/hooks/useAdminFetch';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { parseApiError } from '@/lib/apiError';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/admin/posts/')({
  component: AdminPostsPage,
});

function AdminPostsPage() {
  const { fetchAdmin } = useAdminFetch();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 400);
  const [status, setStatus] = useState<string>('');
  const [deletePost, setDeletePost] = useState<{ id: number; title: string } | null>(null);

  useEffect(() => {
    void debouncedSearch;
    setPage(1);
  }, [debouncedSearch]);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-posts', page, debouncedSearch, status],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), limit: '10' });
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (status) params.set('status', status);
      const res = await fetchAdmin(`/api/admin/blog/posts?${params}`);
      if (!res.ok) throw new Error('Failed to fetch');
      const json = await res.json();
      return json.data;
    },
  });

  const posts = data?.items ?? [];
  const pagination = data ?? {};
  const totalPages = pagination.totalPages ?? 1;

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetchAdmin(`/api/admin/blog/posts/${id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        const message = await parseApiError(res, 'Xóa thất bại');
        throw new Error(message);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-posts'] });
      toast.success('Đã xóa bài viết');
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Xóa thất bại');
    },
  });

  const handleDeleteClick = (post: { id: number; title: string }) => {
    setDeletePost(post);
  };

  const handleConfirmDelete = async () => {
    if (!deletePost) return;
    await deleteMutation.mutateAsync(deletePost.id);
  };

  const isInitialLoading = isLoading && !data;

  return (
    <div>
      {isInitialLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500" />
        </div>
      ) : (
        <>
          <h1 className="text-2xl font-bold text-white mb-6">Quản lý Bài viết</h1>

          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-500" />
              <Input
                type="text"
                placeholder="Tìm kiếm..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={cn('w-full pl-10', adminInputClass)}
              />
            </div>
            <Select value={status || 'all'} onValueChange={(v) => setStatus(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[180px] border-gray-600 bg-gray-800 text-white hover:bg-gray-700/50 focus:ring-2 focus:ring-purple-500">
                <SelectValue placeholder="Tất cả trạng thái" />
              </SelectTrigger>
              <SelectContent className="border-gray-700 bg-gray-800 text-white [&_[data-slot=select-item]]:focus:bg-gray-700">
                <SelectItem value="all">Tất cả trạng thái</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="published">Published</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
              <span className="text-sm text-gray-400">Danh sách bài viết</span>
              <Link to="/admin/posts/new">
                <Button
                  type="button"
                  className="flex items-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white border-0 shadow-lg shadow-purple-500/20"
                >
                  <Plus className="size-4" />
                  Tạo bài mới
                </Button>
              </Link>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-800">
                    <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">
                      Tiêu đề
                    </th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">
                      Trạng thái
                    </th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">
                      Tác giả
                    </th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">Ngày</th>
                    <th className="text-right px-6 py-4 text-sm font-medium text-gray-400">
                      Thao tác
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {posts.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-16 text-center">
                        <p className="text-gray-500 mb-4">Chưa có bài viết nào</p>
                      </td>
                    </tr>
                  ) : (
                    posts.map(
                      (post: {
                        id: number;
                        title: string;
                        status: string;
                        author: { name: string };
                        createdAt: string;
                        publishedAt: string | null;
                      }) => (
                        <tr key={post.id} className="border-b border-gray-800 hover:bg-gray-800/50">
                          <td className="px-6 py-4">
                            <Link
                              to="/admin/posts/$postId/edit"
                              params={{ postId: String(post.id) }}
                              className="text-white hover:text-purple-400 font-medium text-left"
                            >
                              {post.title}
                            </Link>
                          </td>
                          <td className="px-6 py-4">
                            <Badge
                              variant="outline"
                              className={
                                post.status === 'published'
                                  ? 'border-green-500/30 bg-green-500/20 text-green-400'
                                  : post.status === 'draft'
                                    ? 'border-yellow-500/30 bg-yellow-500/20 text-yellow-400'
                                    : 'border-gray-500/30 bg-gray-500/20 text-gray-400'
                              }
                            >
                              {post.status}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 text-gray-400">{post.author?.name ?? '-'}</td>
                          <td className="px-6 py-4 text-gray-400">
                            {new Date(post.publishedAt || post.createdAt).toLocaleDateString(
                              'vi-VN'
                            )}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex justify-end gap-1">
                              <Link
                                to="/admin/posts/$postId/edit"
                                params={{ postId: String(post.id) }}
                              >
                                <Button type="button" variant="ghost" size="icon">
                                  <Edit className="size-4" />
                                </Button>
                              </Link>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteClick(post)}
                                disabled={deleteMutation.isPending}
                                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                              >
                                <Trash2 className="size-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      )
                    )
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex justify-center gap-2 px-6 py-4 border-t border-gray-800">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  Trước
                </Button>
                <span className="flex items-center px-4 text-gray-400">
                  {page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  Sau
                </Button>
              </div>
            )}
          </div>

          <ConfirmModal
            open={Boolean(deletePost)}
            onOpenChange={(open) => !open && setDeletePost(null)}
            title="Xóa bài viết"
            description={
              deletePost
                ? `Bạn có chắc muốn xóa bài "${deletePost.title}"? Hành động này không thể hoàn tác.`
                : undefined
            }
            confirmLabel="Xóa"
            cancelLabel="Hủy"
            variant="destructive"
            onConfirm={handleConfirmDelete}
            isLoading={deleteMutation.isPending}
          />
        </>
      )}
    </div>
  );
}
